/**
 * Track 2 Phase 4 — registry-driven `referencePointForDraftTextInput`.
 *
 * The 45-case switch in `draftSharedHelpers.ts:317` mapped DraftStep →
 * which point in `session.points` is the rubber-band reference when the
 * user types a numeric / coordinate input. Registry already declares
 * this through `DraftStepDescriptor.textInputReference` (one of
 * `"last" | "first" | "second" | "none"`), so the switch reduces to a
 * single dispatch helper.
 *
 * ## Mapping
 *
 *  - `"last"`   → `session.points.at(-1) ?? null`  (20 steps)
 *  - `"first"`  → `session.points[0] ?? null`      (23 steps)
 *  - `"second"` → `session.points[1] ?? null`      ( 6 steps)
 *  - `"none"`   → `null`                           (174 steps)
 *
 * Total = 223 = `DRAFT_STEP_LIST.length`. Per-step coverage is enforced
 * by the `Record<DraftStep, DraftStepDescriptor>` typing of the
 * registry.
 */

import type { DraftPoint, DraftSession } from "../draftTypes";
import {
  DRAFT_STEP_REGISTRY,
  type TextInputReference,
} from "./draftStepRegistry";

/**
 * Resolve the rubber-band reference point for the given session at the
 * current `session.step` per the registry's `textInputReference`
 * declaration. Returns `null` when:
 *   - the descriptor declares `"none"` (no rubber band), or
 *   - the indicated point is not yet present in `session.points`.
 *
 * Drop-in replacement for the legacy `referencePointForDraftTextInput`
 * switch in `draftSharedHelpers.ts:317`.
 */
export function resolveReferencePointForDraftTextInput(
  session: DraftSession,
): DraftPoint | null {
  const entry = DRAFT_STEP_REGISTRY[session.step];
  if (!entry) {
    // Unreachable under `Record<DraftStep, ...>` typing of the
    // registry; preserve legacy default for runtime safety against
    // corrupted persisted state.
    return null;
  }
  return resolveByReference(entry.textInputReference, session);
}

/**
 * Resolve the ORTHO (ORTHOMODE / F8) anchor point for the given session
 * at the current `session.step`.
 *
 * ORTHO locks the next pick onto the 0/90° axis of the rubber-band being
 * drawn — `$LASTPOINT`. For nearly every step that anchor is the SAME
 * prior point as the typed-input reference, so this defaults to
 * `textInputReference`. The registry declares `orthoReference` explicitly
 * for the steps whose ORTHO anchor DIVERGES from their typed-input reference:
 *   - `spline-fit-next` (`"last"`) and `arc-sce-center` (`"first"`) — the
 *     preview rubber-bands from a prior point while the typed input is not a
 *     relative coordinate (so `textInputReference` is `"none"`);
 *   - `rotate-ref-new-angle` (`"first"`) — a CLICK measures its angle from the
 *     rotation base (`points[0]`), not the reference end that the typed input
 *     references (`"last"`);
 *   - `revcloud-freehand-path` (`"none"`) — a continuous cursor trace that must
 *     NOT be axis-locked, so its anchor is explicitly suppressed to null even
 *     though its typed input references `"last"`.
 *   - box/area OPPOSITE-corner steps (`"none"`) — `rectangle`/`mtext`/`hatch`/
 *     `revcloud`/`wipeout`/`viewport`/`vpclip` `-second-corner` + `arrayrect-count`
 *     reference `"first"` only for typed relative coords; ortho-anchoring the
 *     diagonal corner would collapse the box to zero area, so ORTHO is
 *     suppressed to null (@codex P2). AutoCAD never ortho-constrains a
 *     two-corner box.
 *
 * Returns null when the resolved reference is `"none"` (no rubber-band
 * anchor — e.g. the FIRST point of a command, scalar/text prompts, freehand
 * traces, or selection-window corners) or the indicated point is not yet
 * present. This is the anchor `orthoLockedCursor` consumes, so a `null` here
 * means ORTHO does not engage — matching AutoCAD, which never constrains the
 * first point and never collapses a selection window onto an axis.
 */
export function resolveOrthoAnchorForDraftSession(
  session: DraftSession,
): DraftPoint | null {
  const entry = DRAFT_STEP_REGISTRY[session.step];
  if (!entry) return null;
  return resolveByReference(
    entry.orthoReference ?? entry.textInputReference,
    session,
  );
}

/**
 * Internal — pure mapping from the four `TextInputReference` enum
 * values to a `session.points` index. Exported for unit tests.
 */
export function resolveByReference(
  reference: TextInputReference,
  session: DraftSession,
): DraftPoint | null {
  switch (reference) {
    case "last":
      return session.points.at(-1) ?? null;
    case "first":
      return session.points[0] ?? null;
    case "second":
      return session.points[1] ?? null;
    case "none":
      return null;
  }
}
